import { createContext, useContext, useState } from 'react';
import { Snackbar } from '@telegram-apps/telegram-ui';

interface ToastContextValue {
  showError: (text: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Провайдер глобального тост-механизма (Snackbar снизу экрана).
 *
 * Монтируется один раз в App.tsx вокруг всех экранов. Любой экран-потомок
 * может получить { showError } через хук useToast() и показать ошибку.
 *
 * Рендерит ОДИН Snackbar — текущее сообщение, если оно есть. Auto-dismiss
 * управляется параметром duration кита; при onClose сбрасываем сообщение.
 *
 * Образец: RewardToast.tsx (Snackbar из @telegram-apps/telegram-ui).
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);

  function showError(text: string) {
    setMessage(text);
  }

  function handleClose() {
    setMessage(null);
  }

  return (
    <ToastContext.Provider value={{ showError }}>
      {children}
      {message !== null && (
        <Snackbar
          onClose={handleClose}
          duration={4000}
          description={message}
        />
      )}
    </ToastContext.Provider>
  );
}

/**
 * Хук для показа ошибки через глобальный тост.
 *
 * Использование:
 *   const { showError } = useToast();
 *   showError(humanizeApiError(err, t));
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}
